import { JSX } from "react";
import { Pagination } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import styled from "styled-components";
import Select from "../../components/Select/Select";
import color from "../../resource/color";

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
export const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

/** Any value that isn't one of the offered sizes (undefined, NaN, …) falls back to 10. */
export function normalizePageSize(value: unknown): number {
  const n = Number(value);
  return PAGE_SIZE_OPTIONS.includes(n) ? n : DEFAULT_PAGE_SIZE;
}

const Bar = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-top: 16px;
`;

// Page buttons rendered as one joined, bordered strip (prev · pages · next).
const JoinedPagination = styled(Pagination)`
  &.ant-pagination {
    display: flex;
    align-items: stretch;
  }
  .ant-pagination-prev,
  .ant-pagination-next,
  .ant-pagination-item,
  .ant-pagination-jump-prev,
  .ant-pagination-jump-next {
    margin: 0 !important;
    min-width: 44px;
    height: 44px;
    line-height: 42px;
    border: 1px solid ${color.border};
    border-radius: 0;
    background: ${color.white};
  }
  .ant-pagination-prev + li,
  .ant-pagination-item + li,
  .ant-pagination-jump-prev + li,
  .ant-pagination-jump-next + li {
    border-left: none;
  }
  .ant-pagination-prev {
    border-radius: 8px 0 0 8px;
  }
  .ant-pagination-next {
    border-radius: 0 8px 8px 0;
  }
  .ant-pagination-item a {
    font-size: 18px;
    color: ${color.Text2};
  }
  .ant-pagination-item-active a {
    color: ${color.primary};
  }
  .ant-pagination-prev .ant-pagination-item-link,
  .ant-pagination-next .ant-pagination-item-link {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    border: none;
  }
  .ant-pagination-disabled .ant-pagination-item-link {
    color: ${color.Grey};
  }
`;

interface Props {
  current: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

function TierTablePagination({ current, pageSize, total, onChange, onPageSizeChange }: Props): JSX.Element | null {
  if (total <= 0) return null;
  return (
    <Bar>
      <div />
      <JoinedPagination
        current={current}
        pageSize={normalizePageSize(pageSize)}
        total={total}
        onChange={onChange}
        showSizeChanger={false}
        showLessItems
        itemRender={(_, type, original) => {
          if (type === "prev") return <LeftOutlined />;
          if (type === "next") return <RightOutlined />;
          return original;
        }}
      />
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <div style={{ width: 130 }}>
          <Select
            fontSize={14}
            value={normalizePageSize(pageSize)}
            data={PAGE_SIZE_OPTIONS.map((size) => ({ key: size, value: size, label: `${size} / หน้า` }))}
            // The shared Select is clearable by default; clearing a page size gave Number(undefined) = NaN.
            allowClear={false}
            onChange={(v) => onPageSizeChange(normalizePageSize(v))}
          />
        </div>
      </div>
    </Bar>
  );
}

export default TierTablePagination;
